import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';

interface CustomerCodeStepProps {
  customerId: string;
  saveCustomerCode: boolean;
  loading: boolean;
  error?: string;
  success?: string;
  onCustomerIdChange: (value: string) => void;
  onSaveCustomerCodeChange: (checked: boolean) => void;
  onNext: (customerId?: string) => void;
  onSignupClick: () => void;
}

export const CustomerCodeStep: React.FC<CustomerCodeStepProps> = ({
  customerId,
  saveCustomerCode,
  loading,
  error,
  success,
  onCustomerIdChange,
  onSaveCustomerCodeChange,
  onNext,
  onSignupClick,
}) => {
  // 입력 중인 값을 임시로 저장하는 로컬 state
  const [localCustomerId, setLocalCustomerId] = useState(customerId);
  
  // customerId가 외부에서 변경되면 로컬 state도 업데이트 (초기 로드 시)
  useEffect(() => {
    setLocalCustomerId(customerId);
  }, [customerId]);
  
  // "다음" 버튼 클릭 시에만 실제 customerId 업데이트
  const handleNext = () => {
    // 먼저 customerId를 업데이트하고, onNext에 새로운 값을 전달
    onCustomerIdChange(localCustomerId);
    onNext(localCustomerId);
  };
  return (
    <Card className="bg-white shadow-2xl border-0">
      <CardHeader className="pt-6">
        <CardTitle className="text-gray-900 text-xl">고객사 확인</CardTitle>
        <CardDescription className="text-gray-600">
          고객사 코드 또는 별칭(alias)을 입력하세요
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pb-6">
        {error && (
          <AlertMessage 
            type="error" 
            message={error} 
            onClose={() => {}} 
          />
        )}
        {success && (
          <AlertMessage 
            type="success" 
            message={success} 
            onClose={() => {}} 
          />
        )}
        <div className="space-y-2">
          <Label htmlFor="customerId" className="text-sm font-medium text-gray-700">고객사 코드</Label>
          <Input
            id="customerId"
            type="text"
            value={localCustomerId}
            onChange={(e) => setLocalCustomerId(e.target.value)}
            placeholder="예: 12345678 또는 mycompany"
            disabled={loading}
            className="text-base h-11"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleNext();
              }
            }}
          />
          <p className="text-xs text-gray-500">
            선택사항입니다. 건너뛰어도 됩니다.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="saveCustomerCode"
            checked={saveCustomerCode}
            onChange={(e) => onSaveCustomerCodeChange(e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            disabled={loading || !localCustomerId.trim()}
          />
          <Label 
            htmlFor="saveCustomerCode" 
            className="text-sm font-normal cursor-pointer"
          >
            이 값 저장하기 (다음에 자동 입력)
          </Label>
        </div>

        <Button 
          type="button"
          onClick={handleNext}
          className="w-full" 
          disabled={loading}
        >
          다음
        </Button>

        <div className="text-center">
          <Button 
            type="button" 
            variant="link"
            className="text-sm"
            onClick={onSignupClick}
            disabled={loading}
          >
            회원가입
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

